import { Label } from "@/components/ui/label";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import HeadingLevelSelect from "@/components/ui/HeadingLevelSelect";
import { resolveFontSize } from "@/utils/headingSize";
import { GOOGLE_FONTS } from "@/utils/fonts";

const FONT_WEIGHTS = [
  { value: "", label: "Site default" },
  { value: "300", label: "Light (300)" },
  { value: "400", label: "Regular (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "Semibold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "800", label: "Extrabold (800)" },
];

const ALIGNMENTS = [
  { value: "", label: "Site default" },
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

/**
 * Reusable style + colour control bundle for any plain-text field stored
 * on a module instance. Reads/writes config keys prefixed with `prefix`
 * (e.g. `logo_` ⇒ `logo_font_family`, `logo_font_weight`, `logo_font_size`,
 * `logo_color`, `logo_text_align`).
 *
 * All fields default to empty → public renderer falls back to Site Styles.
 *
 * Props:
 *  - label:    Section heading (e.g. "Logo Text Style")
 *  - prefix:   Config-key prefix (e.g. "logo_")
 *  - config:   Current module config dict
 *  - update:   (patch) => void — partial updater
 *  - preview:  Optional preview text rendered using the current style
 *  - compact:  If true, render in a single row (fewer rows)
 *  - sizeKey:  Override the size key name (defaults to `${prefix}font_size`).
 *              Useful for legacy fields like `cta_font_size` (no prefix).
 */
export default function TextStyleControls({
  label,
  prefix = "",
  config = {},
  update,
  preview,
  compact = false,
  sizeKey,
  hideAlign = false,
}) {
  const k = {
    family: `${prefix}font_family`,
    weight: `${prefix}font_weight`,
    size: sizeKey || `${prefix}font_size`,
    color: `${prefix}color`,
    align: `${prefix}text_align`,
  };

  const set = (key, val) => update && update({ [key]: val });

  return (
    <div
      className="rounded-md border border-dashed border-border bg-muted/30 p-3 space-y-3"
      data-testid={`text-style-controls-${prefix || "default"}`}
    >
      {label && (
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      )}
      <div className={`grid gap-3 ${compact ? "grid-cols-2 md:grid-cols-5" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
        <div className="space-y-1">
          <Label className="text-xs">Font family</Label>
          <select
            value={config[k.family] || ""}
            onChange={(e) => set(k.family, e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid={`${prefix}font-family`}
          >
            {GOOGLE_FONTS.map((f, i) =>
              f.group ? (
                <optgroup key={i} label={f.group} />
              ) : (
                <option key={f.value} value={f.value}>{f.label === "System Default" ? "Site default" : f.label}</option>
              )
            )}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Font weight</Label>
          <select
            value={config[k.weight] || ""}
            onChange={(e) => set(k.weight, e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid={`${prefix}font-weight`}
          >
            {FONT_WEIGHTS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Heading size</Label>
          <HeadingLevelSelect
            value={config[k.size]}
            onChange={(lvl) => set(k.size, lvl)}
            testId={`${prefix}heading-size`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Colour</Label>
          <ColorPickerInput
            value={config[k.color] || ""}
            onChange={(v) => set(k.color, v)}
            testId={`${prefix}color`}
            compact
          />
        </div>
        {!hideAlign && (
          <div className="space-y-1">
            <Label className="text-xs">Alignment</Label>
            <select
              value={config[k.align] || ""}
              onChange={(e) => set(k.align, e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid={`${prefix}text-align`}
            >
              {ALIGNMENTS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {preview != null && (
        <div className="rounded-md border border-border bg-background p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Live preview</p>
          <div
            style={{
              fontFamily: config[k.family] || "var(--font-body, inherit)",
              fontSize: resolveFontSize(config[k.size]),
              fontWeight: config[k.weight] || "var(--body-weight, 500)",
              color: config[k.color] || "var(--text-color, currentColor)",
              textAlign: config[k.align] || "left",
              lineHeight: 1.3,
            }}
          >
            {preview || "Sample text"}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Resolves the same set of keys into an inline style object — used by
 * the public renderer (`ModularPage.jsx`) so every styled text field
 * picks up its overrides consistently. Returns `undefined` for any key
 * that the admin has left blank so the parent CSS / Site Styles win.
 */
export function buildTextStyle(config = {}, prefix = "", sizeKey) {
  const k = {
    family: `${prefix}font_family`,
    weight: `${prefix}font_weight`,
    size: sizeKey || `${prefix}font_size`,
    color: `${prefix}color`,
    align: `${prefix}text_align`,
  };
  const out = {};
  if (config[k.family]) out.fontFamily = config[k.family];
  if (config[k.size]) out.fontSize = resolveFontSize(config[k.size]);
  if (config[k.weight]) out.fontWeight = config[k.weight];
  if (config[k.color]) out.color = config[k.color];
  if (config[k.align]) out.textAlign = config[k.align];
  return out;
}
